// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { alphabetNavigationStyles } from "@rpsc/Overrides/Widgets/Brand/AlphabetNavigation";
import IconSearch from "@rpsc/XDL/Icons/IconSearch";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { color, xScreen } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

alphabetNavigationStyles.container = {
    css: css`
        border: 1px solid ${color("common.border")};
        border-radius: 2px;
        padding: 12px;
        margin: 32px 0 24px;
        ${xScreen("lte", "md")} {
            padding: 16px;
        }
    `,
};

alphabetNavigationStyles.brandsLettersWrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;
        ${xScreen("lte", "md")} {
            & > div {
                width: 50%;
            }
        }
        ${xScreen("lt", "sm")} {
            flex-direction: column;
            gap: 16px;
            & > div {
                width: 100%;
            }
        }
    `,
};

alphabetNavigationStyles.selectWrapper = {
    css: css`
        display: none;
        ${xScreen("lte", "md")} {
            display: flex;
            flex: 1;
        }
    `,
};

alphabetNavigationStyles.select = {
    labelProps: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    iconProps: {
        size: 9,
        css: css`
            &,
            & > svg {
                height: 13px;
            }
        `,
    },
    cssOverrides: {
        formInputWrapper: css`
            &&&& {
                margin-top: 0;
            }
        `,
    },
};

alphabetNavigationStyles.alphabetSection = {
    css: css`
        display: flex;
        flex: 1;
        flex-direction: column;
        ${xScreen("lte", "md")} {
            display: none;
        }
    `,
};

alphabetNavigationStyles.alphabetContainer = {
    css: css`
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    `,
};

alphabetNavigationStyles.filterSection = {
    css: css`
        display: flex;
        flex: 1;
        ${FormFieldStyle} {
            width: 100%;
        }
    `,
};

alphabetNavigationStyles.filterInput = {
    mergeCss: true,
    cssOverrides: {
        formField: css`
            &&& {
                ${FormInputWrapper} {
                    margin-top: 0;
                }
                ${IconWrapper} {
                    height: 100%;
                    border-radius: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 35px;
                    top: 0;
                    right: 0;
                }
                ${xScreen("lte", "md")} {
                    width: 100%;
                    max-width: 100%;
                }
            }
        `,
    },
    iconProps: {
        src: IconSearch,
        size: 14,
        color: "primary",
    },
    labelProps: {
        size: 16,
        lineHeight: "22px",
        weight: fontConfig.weight.bold,
        css: css`
            margin-bottom: 8px;
            line-height: 22px;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
};

alphabetNavigationStyles.navigationItem = {
    css: css`
        flex: 0 0 auto;
        display: flex;
        margin: 0;
        align-items: center;
    `,
};

alphabetNavigationStyles.titleText = {
    variant: "h5",
    forwardAs: "h2",
    css: css`
        margin-bottom: 8px;
    `,
};

alphabetNavigationStyles.navigationLink = {
    typographyProps: {
        transform: "capitalize",
        size: 16,
        css: css`
            line-height: 22px;
        `,
    },
    css: css`
        padding: 4px;
        border-radius: 2px;
        &:hover {
            & > ${TypographyStyle} {
                color: ${color("common.ui8")};
            }
        }
        ${({ selectedLetter }: any) =>
            selectedLetter &&
            css`
                background-color: ${color("common.ui9")};
                ${TypographyStyle} {
                    color: ${color("common.black")};
                }
            `}
    `,
};

alphabetNavigationStyles.navigationTextEmpty = {
    transform: "capitalize",
    size: 16,
    weight: fontConfig.weight.bold,
    color: "text.link",
    css: css`
        display: flex;
        align-items: center;
        opacity: 0.33;
        cursor: not-allowed;
        line-height: 26px;
    `,
};
